import { useCallback, useState } from "react";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { FileRejection, useDropzone } from "react-dropzone";
import {
    MAX_ORGANIZATION_LOGO_FILE_SIZE_BYTES,
    MAX_ORGANIZATION_LOGO_FILE_SIZE_MB,
} from "@common/constants/organization";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useCreateNativeInvoiceLogoUploadUrl } from "@api/native-invoice.api";

const LOGO_IMAGE_TYPES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "image/webp",
];

const ERROR_CODES: Record<string, string> = {
    "file-too-large": `Max allowed size is ${MAX_ORGANIZATION_LOGO_FILE_SIZE_MB}MB`,
    "file-invalid-type": "Only PNG, JPEG, GIF, and WEBP files are allowed",
};

interface UploadedLogo {
    // Persisted S3 object key; resolved to a URL server-side at render time.
    path: string;
    // Transient public URL for immediate live-preview display.
    publicUrl: string;
}

interface UseNativeInvoiceLogoUploadParams {
    workspaceId: string;
    // `undefined` while the workspace/entity context isn't ready yet; the
    // upload short-circuits on the falsy guard below rather than relying on a
    // sentinel id.
    entityId: number | undefined;
    onUploaded: (logo: UploadedLogo) => void;
}

// Per-invoice logo upload. Reuses the same presigned-upload pattern as the
// entity logo (EntityLogoUploadProvider) but stores the resulting public URL on
// the invoice's renderSnapshot instead of mutating the shared entity logo.
export function useNativeInvoiceLogoUpload({
    workspaceId,
    entityId,
    onUploaded,
}: UseNativeInvoiceLogoUploadParams) {
    // All upload bookkeeping in one state object (no refs): whether an upload is
    // running, and the generation of the active upload. `generation` is bumped
    // when a new upload starts and whenever the entity switches; a completing
    // upload discards its result if the generation has moved on since it began.
    const [upload, setUpload] = useState({ generation: 0, isUploading: false });
    const createUploadUrlMutation = useCreateNativeInvoiceLogoUploadUrl();
    const { error } = useToaster();

    // Invalidate any in-flight upload the moment the "From" entity changes
    // (switching entities clears the logo), using the "adjust state during
    // render" pattern instead of an effect: comparing against the last-seen
    // entityId and bumping the generation in the same render pass avoids the
    // extra render + effect round-trip a `useEffect([entityId])` would need. A
    // stale upload's completion check then naturally fails and its result is
    // discarded.
    const [prevEntityId, setPrevEntityId] = useState(entityId);
    if (entityId !== prevEntityId) {
        setPrevEntityId(entityId);
        setUpload((current) => ({
            ...current,
            generation: current.generation + 1,
        }));
    }

    // Atomically claim the upload slot: bail (resolve `null`) if an upload is
    // already running, otherwise bump the generation, mark uploading, and
    // resolve with the new value. Folding the concurrency guard into this
    // functional update (instead of reading `upload.isUploading` from the
    // render closure) serializes two onDropAccepted calls in the same tick —
    // the second observes the first's `isUploading` and backs off. Reading
    // through the state updater (not a ref) also captures the latest generation
    // even when an entity switch bumped it earlier this render.
    const startUpload = useCallback(
        (): Promise<number | null> =>
            new Promise((resolve) => {
                setUpload((current) => {
                    if (current.isUploading) {
                        resolve(null);
                        return current;
                    }
                    const generation = current.generation + 1;
                    resolve(generation);
                    return { generation, isUploading: true };
                });
            }),
        [],
    );

    // Resolve whether `uploadId` is still the active generation, and mark the
    // upload finished when it is. Reading through the state updater (not a ref)
    // is the ref-free "ignore stale completions" check — after unmount the
    // updater never runs, so a completed upload simply never acts, replacing
    // the old isMounted guard.
    const finishUpload = useCallback(
        (uploadId: number): Promise<boolean> =>
            new Promise((resolve) => {
                setUpload((current) => {
                    const isCurrent = current.generation === uploadId;
                    resolve(isCurrent);
                    return isCurrent
                        ? { ...current, isUploading: false }
                        : current;
                });
            }),
        [],
    );

    // Read whether `uploadId` is still the active generation without mutating
    // the finished flag (used before the terminal finishUpload call).
    const isCurrentUpload = useCallback(
        (uploadId: number): Promise<boolean> =>
            new Promise((resolve) => {
                setUpload((current) => {
                    resolve(current.generation === uploadId);
                    return current;
                });
            }),
        [],
    );

    const handleRejected = useCallback(
        (fileRejections: FileRejection[]) => {
            fileRejections.forEach((rejection) => {
                const errorMessages = rejection.errors
                    .map((fileError) => ERROR_CODES[fileError.code])
                    .filter(Boolean);
                error(
                    errorMessages.length > 0
                        ? `${rejection.file.name} - ${errorMessages.join(", ")}`
                        : `${rejection.file.name} - Unknown error when uploading file`,
                );
            });
        },
        [error],
    );

    const handleAccepted = useCallback(
        async (acceptedFiles: File[]) => {
            // Bail out if the workspace/entity context isn't set yet —
            // drag-and-drop can fire even while the button is disabled, and a
            // missing id would yield an invalid key.
            if (!workspaceId || !entityId) {
                return;
            }
            // Claim the upload slot atomically; `null` means another upload is
            // already in flight, so this drop is a no-op.
            const uploadId = await startUpload();
            if (uploadId === null) {
                return;
            }
            const uploadLogo = async () => {
                try {
                    const file = acceptedFiles[0];

                    const { uploadUrl, path, publicUrl } =
                        await createUploadUrlMutation.mutateAsync({
                            workspaceId,
                            entityId,
                            name: file.name,
                        });

                    await axios.put(uploadUrl, file, {
                        headers: { "Content-Type": file.type },
                    });

                    const isStillCurrentUpload =
                        await isCurrentUpload(uploadId);
                    if (isStillCurrentUpload) {
                        onUploaded({ path, publicUrl });
                    }
                } catch (e) {
                    Sentry.captureException(e, (scope) =>
                        scope.setTag("nativeInvoiceLogoUpload", "true"),
                    );
                    const isStillCurrentUpload =
                        await isCurrentUpload(uploadId);
                    if (isStillCurrentUpload) {
                        error("Failed to upload logo");
                    }
                }
            };
            // Clears the uploading flag when still current; a stale
            // completion leaves the newer upload's flag untouched.
            await uploadLogo().finally(() => finishUpload(uploadId));
        },
        [
            createUploadUrlMutation,
            workspaceId,
            entityId,
            onUploaded,
            error,
            startUpload,
            isCurrentUpload,
            finishUpload,
        ],
    );

    const { getRootProps, getInputProps, isDragAccept, open } = useDropzone({
        onDropRejected: handleRejected,
        onDropAccepted: handleAccepted,
        accept: LOGO_IMAGE_TYPES,
        maxSize: MAX_ORGANIZATION_LOGO_FILE_SIZE_BYTES,
        multiple: false,
        noClick: true,
    });

    return {
        isDragActive: isDragAccept,
        inputProps: getInputProps(),
        rootProps: getRootProps(),
        isUploading: upload.isUploading,
        open,
    };
}
